import { hasValue } from '@repo/utils';
import type { ECharts } from 'echarts';
import { action, computed, makeObservable, observable } from 'mobx';

import type { FactTrajectoryPointEntity } from '../../service/entities';
import type { TrajectoryService } from '../../service/trajectory-service';
import { MAIN_TRAJECTORY_LINE_WIDTH, REFERENCE_TRAJECTORY_LINE_WIDTH } from '../../utils/trajectory-line-colors';

import {
  DEFAULT_VISIBLE_2D_CHART_IDS,
  getTrajectory2dChartConfig,
  TRAJECTORY_2D_CHART_IDS,
  type Trajectory2dChartConfig,
  type Trajectory2dChartTypeId,
} from './trajectory-2d-charts';
import { downloadChartScreenshot } from './utils/download-chart-screenshot';

export type Trajectory3dChartPoint = [we: number, ns: number, tvd: number];

export type Trajectory3dLine = {
  label: string;
  color: string;
  lineWidth: number;
  data: Trajectory3dChartPoint[];
};

export type Trajectory3dViewControlState = {
  alpha?: number;
  beta?: number;
  distance?: number;
  center?: number[];
};

export type TrajectoryChart2dSettings = {
  order: Trajectory2dChartTypeId[];
  visibility: Partial<Record<Trajectory2dChartTypeId, boolean>>;
};

export const MIN_VISIBLE_2D_CHARTS = 1;

function mapTrajectoryPoints3D(points: FactTrajectoryPointEntity[]): Trajectory3dChartPoint[] {
  return points
    .map((point) => {
      const { we, ns, tvd } = point.data;

      if (!hasValue(we) || !hasValue(ns) || !hasValue(tvd)) {
        return null;
      }

      return [we, ns, -tvd] satisfies Trajectory3dChartPoint;
    })
    .filter(hasValue)
    .sort((a, b) => b[2] - a[2]);
}

function mergeChart2dOrder(savedOrder: Trajectory2dChartTypeId[] | undefined): Trajectory2dChartTypeId[] {
  const knownIds = new Set(TRAJECTORY_2D_CHART_IDS);
  const result: Trajectory2dChartTypeId[] = [];

  for (const id of savedOrder ?? []) {
    if (knownIds.has(id) && !result.includes(id)) {
      result.push(id);
    }
  }

  for (const id of TRAJECTORY_2D_CHART_IDS) {
    if (!result.includes(id)) {
      result.push(id);
    }
  }

  return result;
}

function buildDefaultVisibility(): Partial<Record<Trajectory2dChartTypeId, boolean>> {
  const visibility: Partial<Record<Trajectory2dChartTypeId, boolean>> = {};

  for (const id of TRAJECTORY_2D_CHART_IDS) {
    visibility[id] = DEFAULT_VISIBLE_2D_CHART_IDS.includes(id);
  }

  return visibility;
}

export class TrajectoryGraphStore {
  private readonly trajectoryService: TrajectoryService;

  @observable chartDimension: '2d' | '3d' = '2d';

  @observable.ref trajectory3dViewControl: Trajectory3dViewControlState | null = null;

  @observable.shallow chart2dOrder: Trajectory2dChartTypeId[] = [...TRAJECTORY_2D_CHART_IDS];

  @observable chart2dVisibility: Partial<Record<Trajectory2dChartTypeId, boolean>> = buildDefaultVisibility();

  @observable.ref private chartInstances: ECharts[] = [];

  constructor(trajectoryService: TrajectoryService) {
    this.trajectoryService = trajectoryService;

    makeObservable(this);
  }

  @computed
  get canDownloadScreenshot(): boolean {
    return this.chartInstances.length > 0;
  }

  @computed
  get graphTrajectories() {
    return this.trajectoryService.graphTrajectories;
  }

  @computed
  get mainTrajectory3dLines(): Trajectory3dLine[] {
    return this.graphTrajectories
      .map((trajectory) => {
        const data = mapTrajectoryPoints3D(trajectory.entity.points);

        if (!data.length) {
          return null;
        }

        return {
          label: trajectory.title,
          color: trajectory.color,
          lineWidth: trajectory.role === 'main' ? MAIN_TRAJECTORY_LINE_WIDTH : REFERENCE_TRAJECTORY_LINE_WIDTH,
          data,
        } satisfies Trajectory3dLine;
      })
      .filter(hasValue);
  }

  @computed
  get chart2dSettings(): TrajectoryChart2dSettings {
    return {
      order: [...this.chart2dOrder],
      visibility: { ...this.chart2dVisibility },
    };
  }

  @computed
  get enabledChart2dConfigs(): Trajectory2dChartConfig[] {
    return this.chart2dOrder
      .filter((id) => this.chart2dVisibility[id] !== false)
      .map((id) => getTrajectory2dChartConfig(id))
      .filter(hasValue);
  }

  @computed
  get visible2dChartsCount(): number {
    return this.enabledChart2dConfigs.length;
  }

  @action.bound
  setChartDimension(dimension: '2d' | '3d') {
    this.chartDimension = dimension;
  }

  @action.bound
  setTrajectory3dViewControl(viewControl: Trajectory3dViewControlState) {
    this.trajectory3dViewControl = viewControl;
  }

  @action.bound
  setChart2dSettings(settings: TrajectoryChart2dSettings | undefined) {
    this.chart2dOrder = mergeChart2dOrder(settings?.order);

    if (settings?.visibility) {
      const visibility = buildDefaultVisibility();

      for (const id of TRAJECTORY_2D_CHART_IDS) {
        if (settings.visibility[id] !== undefined) {
          visibility[id] = settings.visibility[id];
        }
      }

      // Ensure at least one chart stays visible.
      if (!TRAJECTORY_2D_CHART_IDS.some((id) => visibility[id] !== false)) {
        visibility[DEFAULT_VISIBLE_2D_CHART_IDS[0]] = true;
      }

      this.chart2dVisibility = visibility;
    } else {
      this.chart2dVisibility = buildDefaultVisibility();
    }
  }

  @action.bound
  setChart2dOrder(order: Trajectory2dChartTypeId[]) {
    this.chart2dOrder = mergeChart2dOrder(order);
  }

  @action.bound
  setChart2dVisibility(id: Trajectory2dChartTypeId, visible: boolean) {
    if (!visible && this.visible2dChartsCount <= MIN_VISIBLE_2D_CHARTS && this.chart2dVisibility[id] !== false) {
      return;
    }

    this.chart2dVisibility = {
      ...this.chart2dVisibility,
      [id]: visible,
    };
  }

  isChart2dVisibilityDisabled(id: Trajectory2dChartTypeId): boolean {
    const isVisible = this.chart2dVisibility[id] !== false;

    return isVisible && this.visible2dChartsCount <= MIN_VISIBLE_2D_CHARTS;
  }

  @action.bound
  registerChartInstance(chart: ECharts) {
    if (!this.chartInstances.includes(chart)) {
      this.chartInstances = [...this.chartInstances, chart];
    }
  }

  @action.bound
  unregisterChartInstance(chart: ECharts) {
    this.chartInstances = this.chartInstances.filter((instance) => instance !== chart);
  }

  @action.bound
  downloadScreenshot() {
    if (!this.chartInstances.length) {
      return;
    }

    const chartsInVisualOrder = [...this.chartInstances].sort((left, right) => {
      const leftRect = left.getDom().getBoundingClientRect();
      const rightRect = right.getDom().getBoundingClientRect();

      return leftRect.top - rightRect.top || leftRect.left - rightRect.left;
    });

    downloadChartScreenshot(chartsInVisualOrder, `trajectory-${this.chartDimension}`);
  }
}
